import core, { App, View } from '@gin-betting/core';
import { includes, transform } from 'lodash';
import { createSelector } from 'reselect';

import {
    DEFAULT_STREAM_URL,
    SportEventMetaKey,
    VIEWABLE_SPORT_LIST,
} from 'app-constants';
import { CONFIG_BY_SPORTS } from 'scoreMetaConfig';
import { MatchPartInfo, State } from 'types';
import {
    filterScoresBySportAndMatchPart,
    getCurrentMatchPart,
    getRouteBySportType,
} from 'utils';

const {
    entities: {
        selectors: {
            makeSelectMatchViewScores,
            selectMatch,
            makeMatchSelector,
            selectMobileMatchStream,
            selectMatchMeta,
            selectSportBySportEvent,
            selectSportByTournament,
        },
    },
    utils: { getSportTypeBySport },
} = core;

const scoreOrderBySport: {
    [key: string]: Partial<{ [key in App.ScoreTypes]: number }>;
} = transform(CONFIG_BY_SPORTS, (acc, { scoresOrder }, sportId) => {
    if (scoresOrder) {
        acc[sportId] = scoresOrder;
    }
});

const selectMatchViewScores = makeSelectMatchViewScores(scoreOrderBySport);

const makeSelectFilteredScores = (isOverview: boolean) =>
    createSelector(
        selectMatch,
        selectMatchViewScores,
        selectMatchMeta,
        (match, scores, meta) => {
            let currentMatchPart: MatchPartInfo | null = null;

            if (match) {
                currentMatchPart = getCurrentMatchPart(meta, match);

                return {
                    scores: filterScoresBySportAndMatchPart(
                        match.sportId,
                        currentMatchPart,
                        scores,
                        isOverview
                    ),
                    currentMatchPart,
                    meta,
                };
            }

            return { scores, currentMatchPart, meta };
        }
    );

export const selectFilteredScores = makeSelectFilteredScores(false);
export const selectOverviewScores = makeSelectFilteredScores(true);

const makeSelectMetaValueByName = <T extends SportEventMetaKey>(metaName: T) =>
    createSelector(
        selectMatchMeta,
        meta => meta.find(m => m.name === metaName)?.value ?? null
    );

const selectBetradarLMTCoverageLevel = makeSelectMetaValueByName(
    SportEventMetaKey.BetradarLMTCoverageLevel
);

export const selectBetradarId = createSelector(
    selectBetradarLMTCoverageLevel,
    makeSelectMetaValueByName(SportEventMetaKey.BetradarId),
    (lmt, betradarId) => (lmt && lmt !== '1.00' ? betradarId : null)
);
export const selectMatchTime = makeSelectMetaValueByName(
    SportEventMetaKey.Time
);
export const selectTimerRunning = makeSelectMetaValueByName(
    SportEventMetaKey.TimerRunning
);

export const selectIsSportEventViewable = createSelector(
    selectMobileMatchStream,
    selectBetradarId,
    makeMatchSelector({
        propsToPick: ['sportId'],
    }),
    (streamUrl, betradarId, match) => {
        const sportId = match?.sportId;

        const hasValidStream =
            !!streamUrl && streamUrl !== '' && streamUrl !== DEFAULT_STREAM_URL;

        const hasValidBetradarId =
            !!betradarId && !!sportId && includes(VIEWABLE_SPORT_LIST, sportId);

        return {
            hasValidStream,
            hasValidBetradarId,
        };
    }
);

type SelectSport = (state: State, props: object) => View.Sport | undefined;

const makeSelectRouteBySportEvent = (selectSport: SelectSport) =>
    createSelector(selectSport, sport =>
        getRouteBySportType(getSportTypeBySport(sport))
    );

export const selectRouteBySportEvent = makeSelectRouteBySportEvent(
    selectSportBySportEvent
);

export const selectRouteByTournament = makeSelectRouteBySportEvent(
    selectSportByTournament
);
